import { FormControl } from "@angular/forms";
import { Transform, Type } from "class-transformer";
import { FORM_TEMPLATE_STATUS } from "../../services/forms/contract/form-template.contract";
import { BaseModel } from "../base.model";
import { BrandingTypeEnum, VariantDeliveryMethodEnum } from "./contract/form-template-contract";
import { FormAnswer, FormAnswerObject } from "./form-answer.model";
import { FormTemplateCatgory } from "./form-template-category.model";
import { FormTemplateEmail } from "./form-template-email.model";
import { FormTemplateGroup } from "./form-template-group.model";
import { FormTemplateQuestion, QuestionTypeEnum } from "./form-template-question.model";
import { FormTemplateSection, FormTemplateSectionType } from "./form-template-section.model";
import { FormTemplateVersion } from "./form-template-version.model";
import { QuestionText } from "./question-text.model";

export class FormTemplate extends BaseModel {
  id: number;
  uuid: string;
  name: string;
  internalName: string;
  @Transform(({ value }) => decodeURIComponent(value || ""))
  description: string;
  minResponsesForAbrv: number;
  minResponsesForFull: number;
  formTemplateCategoryId: number;
  formTemplateVersionId: number;
  @Type(() => Boolean)
  hasSMC: boolean;
  @Type(() => Boolean)
  isCFET: boolean;
  @Type(() => Boolean)
  isAvailableOffline: boolean;
  brandingType: BrandingTypeEnum;
  @Type(() => Boolean)
  useIcons: boolean;
  @Type(() => Boolean)
  isDraft: boolean;
  @Type(() => Boolean)
  isArchived: boolean;
  timerInMinutes: number | null;
  alertBeforeEndTimerInMinutes: number | null;
  image: string | null;
  @Type(() => Boolean)
  hasBeenPreviewed: boolean;
  @Transform(({ value }) => decodeURIComponent(value || ""))
  onlineThankyouMessage: string | null;
  @Transform(({ value }) => decodeURIComponent(value || ""))
  offlineThankyouMessage: string | null;
  @Type(() => FormTemplateSection)
  formTemplateSections: FormTemplateSection[];
  @Type(() => FormTemplateVersion)
  formTemplateVersion: FormTemplateVersion;
  @Type(() => FormTemplateCatgory)
  formTemplateCategory: FormTemplateCatgory;
  @Type(() => FormTemplateEmail)
  formTemplateEmails: FormTemplateEmail[];
  hasAnswers?: boolean | null;
  formTemplateGroupCount?: number | null;
  formTemplateGroups?: FormTemplateGroup[] | null; // { id: number; name: string; status: string }[] | null;
  lineage: string | null;
  sourceTemplateId: number | null;

  public get status(): string {
    if (this.isDraft) {
      return FORM_TEMPLATE_STATUS.Draft;
    } else if (this.isArchived) {
      return FORM_TEMPLATE_STATUS.Archived;
    } else {
      return FORM_TEMPLATE_STATUS.Published;
    }
  }

  public collectFormControls(
    answersToDefaultTo: FormAnswerObject = {}
  ): Record<string, FormControl> {
    return this.questions.reduce(
      (acc, curr) => ({ ...acc, ...curr.collectFormControl(answersToDefaultTo) }),
      {}
    );
  }

  public collectCommentsFormControls(
    answersToDefaultTo: FormAnswerObject = {}
  ): Record<string, FormControl> {
    return this.collectAnsweredComments(answersToDefaultTo).reduce((acc, curr) => {
      // new condtion to handle list question type for comments
      const control = curr.collectFormControl(answersToDefaultTo);
      const key = Object.keys(control)[0];
      const formControl = control[key];
      if (
        typeof formControl.value === "object" &&
        formControl.value !== null &&
        !Array.isArray(formControl.value)
      ) {
        // If value is a JSON object, set it to null or handle as needed
        formControl.setValue(formControl.value.text);
      } else if (
        typeof formControl.value === "object" &&
        formControl.value !== null &&
        Array.isArray(formControl.value)
      ) {
        const firstWithText = formControl.value.find(
          (item: any) => !!item.text && item.text.trim() !== ""
        );
        formControl.setValue(firstWithText ? firstWithText.text : null);
      }
      return { ...acc, ...{ [key]: formControl } };
    }, {});
  }

  public get questions(): FormTemplateQuestion[] {
    return this.formTemplateSections.reduce(
      (sectionsAcc, sectionCurr) => [...sectionsAcc, ...sectionCurr.questions],
      []
    );
  }

  public collectAnsweredComments(answers: FormAnswerObject = {}): QuestionText[] {
    const answeredComments = this.questions.filter((question) => {
      // new list question type added for comments
      if (
        (question.type === QuestionTypeEnum.LongResponse ||
          question.type === QuestionTypeEnum.ShortResponse ||
          question.type === QuestionTypeEnum.List) &&
        answers[question.id] !== undefined &&
        !Array.isArray(answers[question.id])
      ) {
        const textAnswer = answers[question.id] as FormAnswer;
        return !!textAnswer.editedStringData || !!textAnswer.stringData;
      }
      // new condtion to handle list type question with multipal selection for comments
      else if (
        question.type === QuestionTypeEnum.List &&
        answers[question.id] !== undefined &&
        Array.isArray(answers[question.id])
      ) {
        const textAnswers = answers[question.id] as FormAnswer[];
        return textAnswers.some((ans) => !!ans.editedStringData || !!ans.stringData);
      }
      return false;
    }) as QuestionText[];
    return answeredComments;
  }

  /**
   * @description A FormTemplate is valid for creation if it has at least 1 question section, and all sections have at least 1 question
   * @returns boolean
   */
  get isValidForCompletion(): boolean {
    let valid = true;
    const questionSections = this.formTemplateSections.filter(
      (section) => section.formTemplateSectionType.name === FormTemplateSectionType.Question
    );
    if (!questionSections.length || questionSections.some((section) => !section.questions.length)) {
      valid = false;
    }
    return valid;
  }

  get code(): string {
    return `Q-${String(this.id).padStart(3, "0")}`;
  }

  questionCount(variantDeliveryMethod: VariantDeliveryMethodEnum): number {
    let count = 0;
    for (let x = 0; x < this.formTemplateSections.length; x++) {
      const section = this.formTemplateSections[x];
      count += section.questionCount(variantDeliveryMethod);
    }
    return count;
  }
}
